import React from "react";
import {
  FaFile,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileAlt,
  FaFileImage,
  FaTimes,
  FaSpinner,
  FaExclamationCircle
} from "react-icons/fa";
import clsx from "clsx";

interface FilePreviewChipProps {
  fileName: string;
  fileSize: number;
  fileType: string;
  onRemove: () => void;
  isSentAttachment?: boolean;
  isUploading?: boolean;
  hasError?: boolean;
  previewUrl?: string; 
  downloadUrl?: string;  // NEW
  onImageClick?: (url: string) => void;  // ← NEW
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const isImageType = (fileType: string): boolean => {
  const type = fileType.toLowerCase();
  return type.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "bmp", "svg"].some(ext => type.includes(ext));
};

const getFileIcon = (fileType: string) => {
  const type = fileType.toLowerCase();

  // NEW: Image icon
  if (isImageType(type)) {
    return <FaFileImage className="file-preview-icon" style={{ color: "#8b5cf6" }} />;
  }
  if (type.includes("pdf")) {
    return <FaFilePdf className="file-preview-icon" style={{ color: "#dc2626" }} />;
  }
  if (type.includes("doc")) {
    return <FaFileWord className="file-preview-icon" style={{ color: "#2563eb" }} />;
  }
  if (type.includes("xls") || type.includes("csv")) {
    return <FaFileExcel className="file-preview-icon" style={{ color: "#16a34a" }} />;
  }
  if (type.includes("ppt") || type.includes("presentation")) {
    return <FaFilePowerpoint className="file-preview-icon" style={{ color: "#d97706" }} />;
  }
  if (type.includes("txt")) {
    return <FaFileAlt className="file-preview-icon" style={{ color: "#6b7280" }} />;
  }

  return <FaFile className="file-preview-icon" style={{ color: "#4a9d6f" }} />;
};

export const FilePreviewChip: React.FC<FilePreviewChipProps> = ({
  fileName,
  fileSize,
  fileType,
  onRemove,
  isSentAttachment = false,
  isUploading = false,
  hasError = false,
  previewUrl,
  downloadUrl,     
  onImageClick,
}) => {
  const hasImagePreview = previewUrl && isImageType(fileType);

  const chipClasses = clsx(
    'file-preview-chip',
    isSentAttachment && 'sent-attachment-chip',
    hasError && 'error-chip',
    hasImagePreview && 'image-preview-chip'  // NEW class for image-specific styling
  );

  return (
    <div className={chipClasses}>
      {/* NEW: Show thumbnail for images, icon for everything else */}
      {isUploading ? (
        <FaSpinner className="file-preview-icon fa-spin" />
      ) : hasError ? (
        <FaExclamationCircle className="file-preview-icon" style={{ color: '#dc2626' }} />
      ) : hasImagePreview ? (
        <img
          src={previewUrl}
          alt={fileName}
          className="file-preview-thumbnail"
          onClick={(e) => {
            e.stopPropagation();
            onImageClick?.(previewUrl!);
          }}
          style={{ cursor: onImageClick ? 'zoom-in' : 'default' }}
        />
      ) : (
        getFileIcon(fileType)
      )}

      <div className="file-preview-info">
        {/* NEW: Make filename clickable for downloads if it's not an image */}
        {downloadUrl && !hasImagePreview ? (
          <a 
            href={downloadUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="file-preview-name" 
            title={fileName}
            style={{ textDecoration: 'underline', color: 'inherit' }}
          >
            {fileName}
          </a>
        ) : (
          <div className="file-preview-name" title={fileName}>{fileName}</div>
        )}
        <div className="file-preview-size">
          {hasError ? 'Upload Failed' : formatFileSize(fileSize)}
        </div>
      </div>

      {!isSentAttachment && (
        <button className="file-preview-remove" onClick={onRemove} aria-label="Remove file">
          <FaTimes />
        </button>
      )}
    </div>
  );
};